import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQuery} from '@github-ui/react-query'
import {useMemo, useRef} from 'react'
import type {IdToPullRequestMap} from '../types/pull'
import type {BaseTask} from '../types/task'
import {useLocalStorageCache} from './useLocalStorageCache'

export const PR_MAP_STORAGE_KEY = 'AGENT_SESSIONS_PR_MAP'

export function getSortedResourceIds(tasks: BaseTask[]): string {
  const ids = tasks ? tasks.map(task => task.resource_id) : []
  // Dedupe before sorting
  const uniqueIds = Array.from(new Set(ids))
  return uniqueIds.sort((a, b) => Number(a) - Number(b)).join(',')
}

export const fetchPullRequests = async (tasks: BaseTask[], setCachedPRMap: (prMap: IdToPullRequestMap) => void) => {
  const pullRequestIds = getSortedResourceIds(tasks)

  if (!pullRequestIds) {
    return {}
  }

  const response = await fetch(
    `/copilot/agent-sessions/pull-requests?pull_request_ids=${encodeURIComponent(pullRequestIds)}`,
    {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    },
  )

  if (!response.ok) {
    throw new Error('Failed to fetch pull requests')
  }

  const data = await response.json()
  const pullRequests = data.pull_requests

  // Cache the pull requests to localStorage
  setCachedPRMap(pullRequests)

  return pullRequests
}

export function usePullRequests({
  tasks,
  sessionsPollingInterval = 3000,
  initialPullRequestsMap,
  refetchOnMount = false,
}: {
  tasks: BaseTask[]
  sessionsPollingInterval?: number | false
  initialPullRequestsMap?: IdToPullRequestMap
  refetchOnMount?: boolean
}) {
  const pullRequestIds = getSortedResourceIds(tasks)
  const {value: cachedPRMap, setValue: setCachedPRMap} = useLocalStorageCache<IdToPullRequestMap>(PR_MAP_STORAGE_KEY)
  const isAliveEnabled = isFeatureEnabled('copilot_agent_sessions_alive_updates')

  // The ref will hold our cumulative data to prevent flicker
  const cumulativePullsRef = useRef<IdToPullRequestMap>(initialPullRequestsMap ?? {})

  const query = useQuery<IdToPullRequestMap>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['agent-sessions-pull-requests', pullRequestIds],
    queryFn: async () => await fetchPullRequests(tasks, setCachedPRMap),
    initialData: initialPullRequestsMap ?? cachedPRMap,
    staleTime: refetchOnMount ? 0 : undefined,
    refetchInterval: isAliveEnabled ? false : sessionsPollingInterval,
    refetchIntervalInBackground: false,
    enabled: pullRequestIds.length > 0,
  })

  // When new data comes in, merge it into our cumulative ref.
  // Remove cached PRs that were requested but not returned (deleted, lost access, etc).
  if (query.data) {
    const requestedIds = new Set(tasks.map(t => String(t.resource_id)))
    const returnedIds = new Set(Object.keys(query.data))
    const filteredCache = Object.fromEntries(
      Object.entries(cumulativePullsRef.current).filter(([id]) => {
        return !requestedIds.has(id) || returnedIds.has(id)
      }),
    )
    cumulativePullsRef.current = {
      ...filteredCache,
      ...query.data,
    }
  }

  // We use a memo to ensure our component re-renders when the query is done,
  // returning the complete, cumulative data from the ref.
  const finalMap = useMemo(() => {
    // This memo depends on the query's data. When the fetch completes,
    // query.data changes, this recalculates, and the component re-renders
    // with the full map from the ref.
    return cumulativePullsRef.current
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data])

  return {
    ...query,
    // Always return the cumulative data to prevent the UI from ever emptying out in transitional states
    data: finalMap,
  }
}
